import { Component, OnInit, ViewChild, Input, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DatePipe } from '@angular/common';

import { Store } from '@ngrx/store';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CrpRole } from 'modules/shared/models/crp.models';

import { RecordService } from 'modules/core/services/record.service';
import { DialogService } from 'modules/shared/components/dialog/dialog.service';
import { SnackbarService } from 'app/services/snackbar.service';

import { AppState } from 'store/root-reducer';
import { CrpRoleDataSource } from './crp-role.data-source';

@Component({
  selector: 'artemis-crp-role',
  templateUrl: './crp-role.component.html',
  styleUrls: ['./crp-role.component.scss'],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CrpRoleComponent implements OnInit, OnDestroy {

  private unsubscribe$: Subject<void> = new Subject();

  displayedColumns: string[] = ['role', 'dateAppointed', 'dateResigned', 'actions'];
  _crpId: number;
  @Input() set crpId(value: number) {
    this._crpId = value;
    if (this.dataSource) {
      this.dataSource.disconnect(null);
    }
    this.dataSource = value ? new CrpRoleDataSource(this.store, value) : new MatTableDataSource(this.crpRoles);
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.reload();
  }

  get crpId() {
    return this._crpId;
  }

  @Output() change: EventEmitter<{ type: 'ADD' | 'EDIT' | 'DELETE', role: CrpRole }> = new EventEmitter();

  form: FormGroup;
  dataSource: CrpRoleDataSource | MatTableDataSource<CrpRole>;
  rolesList: string[] = [];
  currentRole: CrpRole;
  currentIndex: number;
  crpRoles: CrpRole[] = [];
  unsubscribe: Subject<any> = new Subject();

  addCrpMode: boolean = true;

  maxDateAppointed: Date = new Date();
  minDateResigned: Date;

  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  constructor(private recordService: RecordService,
              private store: Store<AppState>,
              private formBuilder: FormBuilder,
              private dialog: DialogService,
              private snackBar: SnackbarService,
              private datePipe: DatePipe,
              private cdRef: ChangeDetectorRef) {
    this.dataSource = new MatTableDataSource(this.crpRoles);
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  ngOnInit() {
    this.addCrpMode = true;
    this.buildForm();

    this.recordService.getRoles()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: Array<string>) => {
        this.rolesList = res;
      });
  }

  buildForm(): void {
    this.form = this.formBuilder.group({
      role: [, Validators.required],
      otherRole: [],
      dateAppointed: [],
      dateResigned: []
    });

    this.form.get('role').valueChanges.subscribe((role: string) => {
      if (role && (role.toLowerCase() === 'please specify')) {
        this.form.get('otherRole').setValidators(Validators.required);
      } else {
        this.form.get('otherRole').clearValidators();
      }

      this.form.get('otherRole').updateValueAndValidity();
    });

    this.form.get('dateAppointed').valueChanges.subscribe((dateAppointed: Date) => {
      this.minDateResigned = dateAppointed;
    });
  }

  reload(): void {
    if (this.form) {
      this.form.reset();
    }
    this.currentRole = null;
    this.currentIndex = null;
    if (this.crpId) {
      (<CrpRoleDataSource>this.dataSource).loadCrps();
    } else {
      (<MatTableDataSource<CrpRole>>this.dataSource).data = this.crpRoles;
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    }
  }

  /**
   * @method checkOtherRoleValue
   * Check if the role value is customized and set accordingly
   */
  private checkOtherRoleValue(): void {
    let allRoleValues: Array<string> = [];

    this.rolesList.forEach((roleCategory: any) => {
      allRoleValues = allRoleValues.concat(roleCategory.roles);
    });

    const isOtherRole: boolean = this.currentRole.role &&
                                 (this.currentRole.role.toLowerCase() === 'please specify' ||
                                   !~allRoleValues.indexOf(this.currentRole.role));

    if (isOtherRole) {
      this.form.patchValue({
        'role': 'PLEASE SPECIFY',
        'otherRole': this.currentRole.role
      });
    }
  }

  editRole(role: CrpRole, index?: number): void {
    if (!this.crpId) {
      this.currentIndex = index;
    }

    this.currentRole = role;
    this.addCrpMode = false;
    this.form.patchValue(role);
    this.checkOtherRoleValue();
  }

  deleteRole(role: CrpRole, index?: number): void {
    if (this.crpId) {
      // in edit page should check if this is the last in role list before delete
      if ((<CrpRoleDataSource>this.dataSource).getDataSourceLength() > 1) {
        this.dialog.confirm('Are you sure you want to delete?', 'ROLE DELETE')
          .subscribe((res: string) => {
            if (res === 'submit') {
              this.change.emit({
                type: 'DELETE',
                role
              });
            }
          });
      } else {
        this.snackBar.error(`A CRP needs to have at least one role`);
      }
    } else {
      this.dialog.confirm('Are you sure you want to delete?', 'ROLE DELETE')
        .subscribe((res: string) => {
          if (res === 'submit') {
            this.crpRoles.splice(index, 1);
            this.reload();
          }
        });
    }
  }

  getCrpRoles(): Array<CrpRole> {
    this.cdRef.detectChanges();
    return this.crpRoles;
  }

  submit(): void {
    if (this.form.valid) {
      if (this.form.get('role').value.toLowerCase() === 'please specify') {
        this.form.get('role').setValue(this.form.get('otherRole').value);
      }

      if (this.crpId) {
        if (!this.currentRole || !this.currentRole.id) {
          this.currentRole = <CrpRole>{
            role: this.form.get('role').value,
            dateAppointed: this.datePipe.transform(this.form.get('dateAppointed').value, 'yyyy-MM-dd'),
            dateResigned: this.datePipe.transform(this.form.get('dateResigned').value, 'yyyy-MM-dd')
          };

          this.currentRole.crp = this.crpId;
          this.change.emit({
            type: 'ADD',
            role: this.currentRole
          });
        } else {
          this.currentRole = Object.assign(this.currentRole, <CrpRole>{
            role: this.form.get('role').value,
            dateAppointed: this.datePipe.transform(this.form.get('dateAppointed').value, 'yyyy-MM-dd'),
            dateResigned: this.datePipe.transform(this.form.get('dateResigned').value, 'yyyy-MM-dd')
          });

          this.change.emit({
            type: 'EDIT',
            role: this.currentRole
          });
        }
      } else {
        if (this.currentIndex !== null && this.currentIndex >= 0) {
          this.crpRoles[this.currentIndex] = this.form.value;
        } else {
          this.currentRole = <CrpRole>{
            role: this.form.get('role').value,
            dateAppointed: this.datePipe.transform(this.form.get('dateAppointed').value, 'yyyy-MM-dd'),
            dateResigned: this.datePipe.transform(this.form.get('dateResigned').value, 'yyyy-MM-dd')
          };

          this.crpRoles.unshift(Object.assign({}, this.currentRole));
        }

        this.reload();
      }

      this.currentRole = null;
      this.addCrpMode = true;
    }
  }

  cancelAddRole(): void {
    this.currentRole = null;
    this.addCrpMode = true;

    this.reload();
  }

  clearFormAndTable(): void {
    this.crpRoles = [];
    this.reload();
  }

  /**
   * @method setSelectedRole
   * Set selected role value
   */
  setSelectedRole(role: string): void {
    this.form.get('role').setValue(role);
    this.form.markAsDirty();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

}
